import { useQuery, useQueryClient } from '@tanstack/react-query'

import { RequestFieldsEnum } from '@/enums/RequestFieldsEnum'
import { useOrgScopedQueryCacheKey } from '@/hooks/useOrgScopedQueryCacheKey'
import { cartsClient } from '@/lib/clients/cartsClient'
import { useBaseRequest } from './useBaseRequest'

import type { GetPaymentModesRequest, PaymentModeList } from '@/gen/ecom'

export type { PaymentMode } from '@/gen/ecom'

// Payment modes = all possible payment methods.
// It is only a "dictionary" of all possible payment options (code, description and name)
// To find out possible payment methods for the particular user/cart, use "payment details" calls.
export function usePaymentModes() {
  const { cartBaseRequest } = useBaseRequest()
  const queryClient = useQueryClient()

  const overrides: RequestInit = {
    headers: { 'Content-Type': 'application/json', 'sap-commerce-cloud-user-id': cartBaseRequest.userId },
  }

  const getPaymentModesRequest: GetPaymentModesRequest = {
    ...cartBaseRequest,
    fields: RequestFieldsEnum.Full,
  }

  const paymentModesCacheKey = useOrgScopedQueryCacheKey(cartsClient.getPaymentModes.name, getPaymentModesRequest)

  const getPaymentModes = async (): Promise<PaymentModeList> => {
    // Force refetch of payment modes
    queryClient.invalidateQueries({ queryKey: paymentModesCacheKey })

    return queryClient.fetchQuery({
      queryKey: paymentModesCacheKey,
      queryFn: () => cartsClient.getPaymentModes(getPaymentModesRequest, overrides),
    })
  }

  const paymentModesQuery = useQuery({
    queryKey: paymentModesCacheKey,
    queryFn: getPaymentModes,
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY,
  })

  return {
    getPaymentModes,
    paymentModesQuery,
    paymentModesCacheKey,
  }
}
